import { useState } from "react";
import { Sparkles, Lightbulb, Wand2, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

const btnCls =
  "inline-flex w-full items-center justify-center gap-2 bg-[#0F172A] px-4 py-2.5 font-display text-xs font-bold text-white transition-colors duration-150 hover:bg-[#1e293b] disabled:opacity-50";
const inputCls =
  "w-full border border-slate-200 bg-white px-3 py-2 text-sm text-[#0F172A] focus:border-[#4687FF] focus:outline-none";

async function streamDraft(payload, onToken) {
  const token = localStorage.getItem("vpnr_token");
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ai/draft`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(formatApiErrorDetail(err.detail) || `Request failed (${res.status})`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n\n");
    buf = lines.pop();
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      let msg;
      try {
        msg = JSON.parse(line.slice(6));
      } catch (e) {
        continue;
      }
      if (msg.error) throw new Error(msg.error);
      if (msg.t) onToken(msg.t);
    }
  }
}

export const AiAssistant = ({ form, setForm }) => {
  const [ideas, setIdeas] = useState(null);
  const [busy, setBusy] = useState(null); // suggest | draft | improve | image
  const [instruction, setInstruction] = useState("");
  const [imagePrompt, setImagePrompt] = useState("");

  const suggest = async () => {
    setBusy("suggest");
    try {
      const { data } = await api.post("/admin/ai/suggest", { category: form.category });
      setIdeas(data.ideas);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const pickIdea = (idea) => {
    setForm((f) => ({
      ...f,
      title: idea.title,
      excerpt: idea.angle || f.excerpt,
      meta_title: idea.title,
      meta_description: idea.angle || f.meta_description,
    }));
    setIdeas(null);
    toast.success("Title applied — now generate the draft");
  };

  const writeDraft = async (mode) => {
    if (mode === "draft" && !form.title.trim()) {
      toast.error("Add a title first (or pick a suggestion)");
      return;
    }
    setBusy(mode);
    if (mode === "draft") setForm((f) => ({ ...f, content: "" }));
    let acc = mode === "draft" ? "" : null;
    try {
      if (mode === "improve") {
        let revised = "";
        await streamDraft(
          { mode: "improve", content: form.content, instruction },
          (t) => {
            revised += t;
          }
        );
        if (!revised.trim()) throw new Error("AI returned an empty revision — original kept.");
        setForm((f) => ({ ...f, content: revised }));
      } else {
        await streamDraft(
          { mode: "draft", title: form.title, category: form.category, brief: form.excerpt },
          (t) => {
            acc += t;
            const snapshot = acc;
            setForm((f) => ({ ...f, content: snapshot }));
          }
        );
      }
      toast.success(mode === "draft" ? "Draft generated!" : "Article improved!");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const generateImage = async () => {
    const prompt = imagePrompt.trim() || form.title.trim();
    if (!prompt) {
      toast.error("Enter an image prompt (or add a title)");
      return;
    }
    setBusy("image");
    try {
      const { data } = await api.post("/admin/ai/image", { prompt }, { timeout: 120000 });
      const md = `![${form.title || "Article image"}](${data.url})\n\n`;
      setForm((f) => ({ ...f, content: md + (f.content || "") }));
      toast.success("Image generated and inserted at the top of the article");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const spinner = <Loader2 size={13} className="animate-spin" />;

  return (
    <div className="border-2 border-[#4687FF] bg-[#4687FF]/[0.04] p-6" data-testid="ai-assistant-panel">
      <h3 className="mb-1 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.15em] text-[#2563eb]">
        <Sparkles size={15} /> AI Assistant
      </h3>
      <p className="mb-5 text-xs text-slate-500">Powered by GPT-5.5 + Nano Banana</p>

      <div className="space-y-5">
        <div>
          <button onClick={suggest} disabled={!!busy} data-testid="ai-suggest-btn" className={btnCls}>
            {busy === "suggest" ? spinner : <Lightbulb size={13} />} Suggest article topics
          </button>
          {ideas && (
            <div className="mt-3 space-y-2" data-testid="ai-ideas-list">
              {ideas.map((idea, i) => (
                <button
                  key={i}
                  onClick={() => pickIdea(idea)}
                  data-testid={`ai-idea-${i}`}
                  className="block w-full border border-slate-200 bg-white p-3 text-left transition-colors duration-150 hover:border-[#4687FF]"
                >
                  <span className="block text-[13px] font-bold leading-snug text-[#0F172A]">{idea.title}</span>
                  <span className="mt-1 block text-xs leading-snug text-slate-500">{idea.angle}</span>
                  {idea.keyword && (
                    <span className="mt-1.5 inline-block bg-[#4687FF]/10 px-2 py-0.5 text-[10px] font-bold text-[#2563eb]">
                      {idea.keyword}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-[#4687FF]/20 pt-5">
          <button onClick={() => writeDraft("draft")} disabled={!!busy} data-testid="ai-draft-btn" className={`${btnCls} !bg-[#4687FF] hover:!bg-[#2563eb]`}>
            {busy === "draft" ? spinner : <Wand2 size={13} />} Write full draft from title
          </button>
          <p className="mt-1.5 text-[11px] text-slate-400">~900-word SEO article, streams into the editor.</p>
        </div>

        <div className="border-t border-[#4687FF]/20 pt-5">
          <input
            className={inputCls}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="e.g. make it more concise, add a FAQ section"
            data-testid="ai-improve-instruction"
          />
          <button onClick={() => writeDraft("improve")} disabled={!!busy || !form.content} data-testid="ai-improve-btn" className={`${btnCls} mt-2`}>
            {busy === "improve" ? spinner : <Wand2 size={13} />} Improve current article
          </button>
        </div>

        <div className="border-t border-[#4687FF]/20 pt-5">
          <input
            className={inputCls}
            value={imagePrompt}
            onChange={(e) => setImagePrompt(e.target.value)}
            placeholder="Image prompt (blank = use title)"
            data-testid="ai-image-prompt"
          />
          <button onClick={generateImage} disabled={!!busy} data-testid="ai-image-btn" className={`${btnCls} mt-2`}>
            {busy === "image" ? spinner : <ImagePlus size={13} />} Generate hero image
          </button>
          <p className="mt-1.5 text-[11px] text-slate-400">Inserted as Markdown at the top of the article.</p>
        </div>
      </div>
    </div>
  );
};
